import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { resizeImages } from "@/lib/resizeImage";
import { toast } from "sonner";
import { Camera, Zap, Loader2, CheckCircle2, AlertTriangle, ArrowRight, Trash2, PenLine, UserRound, Sparkles, ImageIcon, Files } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

/**
 * Photo Docket button + multi-snap batch queue.
 *
 * - Single file selected → parse → navigate straight to /review (unchanged).
 * - Multiple files selected → parse each in parallel, show a queue sheet.
 *   From the queue, each parsed docket can be saved as a pending entry in
 *   one tap, opened in /review for a full edit, or discarded. The Queue
 *   page (/queue) then picks the pending entries up for bulk approval.
 *
 * Props:
 *   - activeJobId: id to attach to every batched entry
 *   - supplierSuggestions: forwarded to /review for chip pre-fill
 *   - disabled: hard-lock (e.g. voice recording in progress)
 */
export default function PhotoDocket({ activeJobId, jobs = [], supplierSuggestions = [], disabled = false }) {
  const nav = useNavigate();
  // Two hidden inputs — one for the on-site camera (`capture="environment"`
  // forces the OS camera UI on mobile), one for the phone's photo library.
  // We drop `capture` from the library input so iOS shows the Photos picker
  // (with multi-select) rather than the camera app. Both funnel into the
  // same `onPhoto` handler.
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  // `filesRef` is a third input with a broader `accept` (still image-only,
  // but explicitly listing JPEG/PNG/HEIC MIMEs). On iOS the Files.app path
  // hands us dockets stashed in iCloud / Downloads without going through
  // Photos, which is often faster for accountants sending in scans.
  const filesRef = useRef(null);
  const [sourceSheetOpen, setSourceSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queue, setQueue] = useState([]); // [{ id, filename, thumb, status, parsed?, error? }]
  // Optional "apply to all" job for the batch. `null` = use whatever
  // activeJob is set at the app level (may itself be null → unassigned).
  // We set this from the queue-header chip so tradies can bulk-tag every
  // parsed row before hitting Approve all.
  const [batchJobId, setBatchJobId] = useState(null);
  // Undo bucket — after "Approve all" we remember the entry ids we just
  // flipped to approved. A quiet Sonner toast offers a 30-second window
  // to unwind. Cleared on next batch or on undo.
  const [undoBundle, setUndoBundle] = useState(null); // { ids: [...], toastId }

  const queueRef = useRef([]);
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);
  useEffect(
    () => () => {
      queueRef.current.forEach((q) => q.thumb && URL.revokeObjectURL(q.thumb));
    },
    []
  );

  const parseOne = async (file, entryId) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ai/parse-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQueue((prev) =>
        prev.map((q) =>
          q.id === entryId
            ? { ...q, status: "parsed", parsed: { ...data, job_id: data.job_id || activeJobId } }
            : q
        )
      );
    } catch (err) {
      devLog.warn("batch parse failed", err);
      setQueue((prev) =>
        prev.map((q) => (q.id === entryId ? { ...q, status: "error", error: "Parse failed" } : q))
      );
    }
  };

  const onPhoto = async (e) => {
    const raw = Array.from(e.target.files || []);
    if (!raw.length) return;

    // First-line dedupe: strip File objects the tradie picked twice in the
    // same OS picker session (iOS Photo Library lets you tap the same
    // thumbnail multiple times). Compare by `name+size` — same phone
    // shot always matches; false-positives across distinct receipts with
    // identical name+size are vanishingly rare in a real gallery.
    const dupTracker = new Set();
    const deduped = raw.filter((f) => {
      const key = `${f.name}::${f.size}`;
      if (dupTracker.has(key)) return false;
      dupTracker.add(key);
      return true;
    });
    if (deduped.length < raw.length) {
      toast.message(
        `${raw.length - deduped.length} duplicate${raw.length - deduped.length === 1 ? "" : "s"} skipped`,
      );
    }
    if (!deduped.length) {
      if (e.target) e.target.value = "";
      return;
    }

    // Client-side downsize — a fresh iPhone photo is ~5–8 MB. Bringing it
    // under 500 KB on-device slashes upload time on flaky site 4G AND
    // keeps the accountant pack manageable when 30+ receipts get inlined.
    let files;
    try {
      files = await resizeImages(deduped, { maxEdge: 1200, quality: 0.70 });
    } catch (err) {
      // resizeImages itself never throws (it falls back per-file), so this
      // catch is belt-and-braces. If the whole batch ever fails we ship
      // originals rather than blocking the tradie.
      devLog.warn("photo resize batch failed", err);
      files = deduped;
    }

    // Single-file path — keep behaviour unchanged.
    if (files.length === 1) {
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", files[0]);
        const { data } = await api.post("/ai/parse-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        nav("/review", {
          state: {
            parsed: { ...data, job_id: data.job_id || activeJobId },
            source: "photo",
            supplierSuggestions,
          },
        });
      } catch (err) {
        devLog.warn("parse-photo failed", err);
        toast.error("Photo parse failed. You can still enter manually.");
        nav("/review", {
          state: {
            parsed: { source: "photo", job_id: activeJobId },
            source: "photo",
            supplierSuggestions,
          },
        });
      } finally {
        setBusy(false);
        if (e.target) e.target.value = "";
      }
      return;
    }

    // Multi-file batch path.
    const seed = files.map((f, i) => ({
      id: `${Date.now()}-${i}`,
      filename: f.name || `docket-${i + 1}.jpg`,
      thumb: URL.createObjectURL(f),
      file: f,
      status: "parsing",
    }));
    setQueue(seed);
    setQueueOpen(true);
    if (e.target) e.target.value = "";

    await Promise.allSettled(seed.map((s) => parseOne(s.file, s.id)));
  };

  const removeItem = (id) => {
    setQueue((prev) => {
      const item = prev.find((q) => q.id === id);
      if (item?.thumb) URL.revokeObjectURL(item.thumb);
      return prev.filter((q) => q.id !== id);
    });
  };

  const saveAsPending = async (item) => {
    if (!item.parsed) return null;
    setQueue((prev) =>
      prev.map((q) => (q.id === item.id ? { ...q, status: "saving" } : q))
    );
    const p = item.parsed;
    // Job resolution order: whatever Review might have set on the row →
    // "Apply job to all" chip in the queue → the app-level activeJob →
    // unassigned. Explicit per-row edits win so a tradie can still peel
    // off one docket for a different job.
    const resolvedJobId = p.job_id || batchJobId || activeJobId || null;
    const payload = {
      job_id: resolvedJobId,
      supplier: p.supplier || "",
      description: p.description || "",
      total: Number(p.total || 0),
      gst: Number(p.gst || 0),
      category: p.category || "",
      cost_code: p.cost_code || "",
      notes: p.notes || "",
      deduction_category: p.deduction_category || "materials",
      entry_date: p.entry_date || new Date().toISOString().slice(0, 10),
      source: "photo",
      photo_path: p.photo_path || null,
      confidence: p.confidence || null,
      raw_transcript: "",
      status: "pending",
    };
    try {
      const { data } = await api.post("/entries", payload);
      setQueue((prev) =>
        prev.map((q) => (q.id === item.id ? { ...q, status: "saved", saved_id: data?.id } : q))
      );
      return data?.id || null;
    } catch (err) {
      devLog.warn("save pending failed", err);
      setQueue((prev) =>
        prev.map((q) =>
          q.id === item.id ? { ...q, status: "parsed", error: "Save failed" } : q
        )
      );
      toast.error("Save failed — try opening in Review");
      return null;
    }
  };

  /** Minimum fields needed for a docket to be auto-approvable straight to
      the Ledger from the batch queue. Missing job is OK (many tradies don't
      use jobs); missing supplier / total / date is NOT — those force the
      row into "needs review" and the tradie has to open it manually. */
  const canBatchApprove = (item) => {
    const p = item?.parsed;
    if (!p) return false;
    const supplier = (p.supplier || "").trim();
    const total = Number(p.total || 0);
    const entryDate = (p.entry_date || "").trim();
    const cat = (p.deduction_category || "").trim();
    return Boolean(supplier && total > 0 && entryDate && cat);
  };

  /**
   * "Approve all" — the one-tap batch commit for a multi-photo import.
   * For every Ready row we:
   *   1) Skip rows still marked personal (they need Review to file to
   *      Personal Tax — different data model, different route).
   *   2) Split the rest into COMPLETE (has supplier + total + date + cat)
   *      vs. NEEDS-REVIEW (missing any of those).
   *   3) POST each complete row to /entries as pending, collect its id.
   *   4) Bulk-approve every id in one call → status flips to "approved"
   *      so it lands in the Ledger immediately.
   *   5) Report ONE toast with the exact counts so the tradie can see
   *      what happened without opening the Queue tab.
   */
  const saveAllReady = async () => {
    const ready = queue.filter((q) => q.status === "parsed" && q.parsed);
    if (!ready.length) return;

    const personal = ready.filter((q) => q.forcePersonal || q.parsed?.personal_suggestion);
    const business = ready.filter((q) => !(q.forcePersonal || q.parsed?.personal_suggestion));
    const complete = business.filter(canBatchApprove);
    const incomplete = business.filter((q) => !canBatchApprove(q));

    // Step 1: create the pending entries (sequentially — POST /entries
    // isn't idempotent and firing 30 parallel requests hammers the
    // ATO-stamp write). Collect ids of successful saves for bulk approve.
    const savedIds = [];
    for (const item of complete) {
      const id = await saveAsPending(item);
      if (id) savedIds.push(id);
    }

    // Step 2: flip them all to approved in a single POST. If this fails
    // the rows stay as pending in the Queue — the tradie can still
    // approve manually from there.
    let approvedCount = 0;
    if (savedIds.length) {
      try {
        await api.post("/entries/bulk-approve", savedIds);
        approvedCount = savedIds.length;
        setQueue((prev) =>
          prev.map((q) =>
            savedIds.includes(q.saved_id) ? { ...q, status: "approved" } : q
          )
        );
      } catch (err) {
        devLog.warn("bulk-approve failed", err);
        toast.error(
          `${savedIds.length} saved to Queue but couldn't be approved — head to Queue to approve.`
        );
      }
    }

    // Step 3: one clear success line — tradie's on-site, they don't want
    // to read a wall of text. Also arm the 30-second undo so a mistap on
    // "Approve all" doesn't force manual clean-up in the Ledger.
    if (approvedCount) {
      const parts = [`${approvedCount} saved to Ledger`];
      if (incomplete.length) {
        parts.push(
          `${incomplete.length} need${incomplete.length === 1 ? "s" : ""} details`
        );
      }
      if (personal.length) {
        parts.push(
          `${personal.length} personal need${personal.length === 1 ? "s" : ""} Review`
        );
      }
      // Sonner returns a stable id so we can dismiss the toast on undo.
      const toastId = toast.success(parts.join(" · "), {
        duration: 30000,
        action: {
          label: "Undo",
          onClick: async () => undoBatchApprove(savedIds, toastId),
        },
      });
      setUndoBundle({ ids: [...savedIds], toastId });
    } else if (incomplete.length || personal.length) {
      // Nothing was auto-approved — tell the tradie why.
      const parts = [];
      if (incomplete.length) parts.push(`${incomplete.length} missing details`);
      if (personal.length) parts.push(`${personal.length} personal`);
      toast.message(`Open each in Review — ${parts.join(", ")}`, { duration: 4500 });
    }
  };

  /**
   * Undo a just-completed "Approve all" batch. We reverse each entry back
   * to `pending` status (not delete — the tradie's photos + parsed data
   * are still valid, they just shouldn't be in the Ledger).
   *
   * If the reversal partially fails (network flake), we tell the tradie
   * how many made it and leave the rest to a manual Queue → un-approve.
   */
  const undoBatchApprove = async (ids, toastId) => {
    if (!ids?.length) return;
    if (toastId) toast.dismiss(toastId);
    // Reverse via bulk-approve's mirror endpoint if we had one; the
    // existing status-update accepts { status: "pending" }. Fire sequentially
    // to stay gentle on the write path.
    let reverted = 0;
    for (const id of ids) {
      try {
        await api.put(`/entries/${id}`, { status: "pending" });
        reverted += 1;
      } catch (err) {
        devLog.warn("undo revert failed", err);
      }
    }
    setQueue((prev) =>
      prev.map((q) => (ids.includes(q.saved_id) ? { ...q, status: "saved" } : q))
    );
    setUndoBundle(null);
    if (reverted === ids.length) {
      toast.success(`${reverted} moved back to Queue`);
    } else {
      toast.message(
        `${reverted} of ${ids.length} moved back to Queue — head to Queue to fix the rest.`,
      );
    }
  };

  const openInReview = (item, opts = {}) => {
    if (!item.parsed) return;
    const forcePersonal = opts.forcePersonal || item.forcePersonal;
    const parsed = { ...item.parsed, job_id: item.parsed.job_id || activeJobId };
    if (forcePersonal && !parsed.personal_suggestion) {
      // Manual override — nudge Review to flip the toggle ON with a sensible default kind.
      parsed.personal_suggestion = {
        top_kind: "deduction",
        sub_category: "other",
        reason: "You marked this as personal",
      };
    }
    nav("/review", {
      state: {
        parsed,
        source: "photo",
        supplierSuggestions,
      },
    });
  };

  const toggleForcePersonal = (id) => {
    setQueue((prev) =>
      prev.map((q) =>
        q.id === id ? { ...q, forcePersonal: !q.forcePersonal } : q
      )
    );
  };

  const parsingCount = queue.filter((q) => q.status === "parsing").length;
  const readyCount = queue.filter((q) => q.status === "parsed").length;
  const savedCount = queue.filter((q) => q.status === "saved" || q.status === "approved").length;
  const approvedCount = queue.filter((q) => q.status === "approved").length;
  const errorCount = queue.filter((q) => q.status === "error").length;
  const allDone = queue.length > 0 && parsingCount === 0;

  return (
    <>
      {/* Camera input — `capture="environment"` forces the OS camera UI on
          mobile Safari/Chrome. Not `multiple` (camera takes one shot at a
          time on every mainstream mobile browser). */}
      <input
        ref={cameraRef}
        id="photo-input-camera"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onPhoto}
        className="hidden"
        data-testid="photo-input-camera"
      />
      {/* Library input — NO `capture` attribute so iOS/Android show the
          photo library picker with multi-select. `multiple` allows batch
          selection on-site (5 receipts from lunch, 3 fuel dockets, etc.). */}
      <input
        ref={libraryRef}
        id="photo-input-library"
        type="file"
        accept="image/*"
        multiple
        onChange={onPhoto}
        className="hidden"
        data-testid="photo-input-library"
      />
      {/* Files input — explicit MIME list (no `image/*` wildcard) so iOS
          routes straight to the Files.app picker rather than the "Photo
          Library / Take Photo / Choose Files" native menu it shows for
          wildcard image inputs. Multi-select stays on so a tradie can
          grab a batch of scanned dockets in one go. */}
      <input
        ref={filesRef}
        id="photo-input-files"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        onChange={onPhoto}
        className="hidden"
        data-testid="photo-input-files"
      />
      {/* Backwards-compatible testid — some existing e2e tests target
          `photo-input`; keep it pointing at the camera path so unattended
          runs still land on the same behaviour. */}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onPhoto}
        className="hidden"
        data-testid="photo-input"
        aria-hidden="true"
        tabIndex={-1}
      />
      <button
        type="button"
        data-testid="photo-docket-btn"
        onClick={() => setSourceSheetOpen(true)}
        disabled={disabled || busy}
        className="group h-32 rounded-2xl bi-accent-btn bi-cta-pulse flex items-center px-6 gap-4 disabled:opacity-70 relative overflow-hidden"
      >
        <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center group-active:scale-95 group-hover:rotate-[-4deg] transition-transform">
          <Camera className="w-9 h-9" strokeWidth={2.2} />
        </div>
        <div className="text-left flex-1">
          <div className="font-heading text-2xl font-extrabold leading-tight tracking-tight">
            Photo Receipt
          </div>
          <div className="text-sm text-white/75 leading-snug line-clamp-2">
            Snap or pick from library · AI queues them for review
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/65 font-heading font-bold">
          <Zap className="w-4 h-4" /> Fastest
        </div>
        <Zap className="sm:hidden w-6 h-6 text-white/55 group-hover:scale-110 transition" />
        {/* Subtle radial highlight — feels premium without adding fluff */}
        <span className="absolute -top-16 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        {busy && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center gap-3 text-white font-medium">
            <div className="w-2 h-2 bg-[#FF9500] rounded-full animate-pulse" />
            Reading with AI…
          </div>
        )}
      </button>

      {/* Dark BuildIntel source sheet — matches the pattern used on entry &
          job Attachments panels so tradies see the same 3-row picker
          everywhere. Radix's Sheet + our `bg-[#1F1F1F]` override wins the
          Tailwind cascade (verified — computed style rgb(31,31,31)). */}
      <Sheet open={sourceSheetOpen} onOpenChange={setSourceSheetOpen}>
        <SheetContent
          side="bottom"
          className="bg-[#1F1F1F] border-t border-white/10 text-white p-0 max-h-[70vh] rounded-t-2xl shadow-[0_-24px_60px_-15px_rgba(0,0,0,0.9)]"
          data-testid="photo-source-sheet"
        >
          {/* Orange accent stripe at the very top — an unmistakable
              BuildIntel signal that this is OUR custom sheet, not iOS's
              native file picker. Users who report "still pale system"
              can eyeball this. */}
          <div className="h-1 w-full bg-gradient-to-r from-[#FF9500] via-[#FFB347] to-[#FF9500]" />
          <SheetHeader className="px-5 pt-5 pb-2 text-left">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FF9500]/15 text-[#FF9500] text-[10px] font-heading font-bold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF9500]" />
                BuildIntel
              </span>
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-heading font-bold">
                Receipt source · v2026.02.15-r2
              </span>
            </div>
            <SheetTitle className="font-heading font-extrabold text-lg text-white">
              Add a receipt
            </SheetTitle>
            <SheetDescription className="text-white/60 text-xs leading-relaxed">
              Pick from the library, snap a fresh one, or grab a file from another app.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pt-3 pb-6 space-y-2">
            {/* Using <label htmlFor=...> on iOS Safari preserves the user
                activation gesture through to the OS file picker without
                relying on a JS `.click()` — which some Safari versions
                intermittently swallow. Sheet close is handled by the
                onChange of each input after selection. */}
            <label
              htmlFor="photo-input-library"
              data-testid="photo-source-library"
              onClick={() => setSourceSheetOpen(false)}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30 cursor-pointer select-none"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <ImageIcon className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Photo Library</div>
                <div className="text-[11px] text-white/50">Pick one or more photos from your gallery</div>
              </span>
            </label>
            <label
              htmlFor="photo-input-camera"
              data-testid="photo-source-camera"
              onClick={() => setSourceSheetOpen(false)}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30 cursor-pointer select-none"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <Camera className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Take Photo</div>
                <div className="text-[11px] text-white/50">Opens the camera on your phone</div>
              </span>
            </label>
            <label
              htmlFor="photo-input-files"
              data-testid="photo-source-files"
              onClick={() => setSourceSheetOpen(false)}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30 cursor-pointer select-none"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <Files className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Choose Files</div>
                <div className="text-[11px] text-white/50">Grab a receipt image from Files or another app</div>
              </span>
            </label>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={queueOpen}
        onOpenChange={(o) => {
          if (!o) {
            // clean up blob urls when closing
            queue.forEach((q) => q.thumb && URL.revokeObjectURL(q.thumb));
            setQueue([]);
          }
          setQueueOpen(o);
        }}
      >
        <SheetContent
          side="bottom"
          className="bg-[#1F1F1F] border-white/10 text-white p-0 max-h-[85vh] overflow-y-auto"
          data-testid="photo-queue-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-2 text-left">
            <SheetTitle className="font-heading font-extrabold text-xl">
              Receipt queue · {queue.length}
            </SheetTitle>
            <SheetDescription className="text-white/60 text-sm">
              {parsingCount > 0
                ? `Reading ${parsingCount} of ${queue.length}… tap any card to review.`
                : errorCount > 0
                ? `${readyCount} ready · ${errorCount} need manual review`
                : approvedCount > 0
                ? `${approvedCount} in Ledger · ${readyCount} still ready`
                : `${readyCount} ready — one tap to send to Ledger`}
            </SheetDescription>
            {/* Bulk job override — a compact chip so tradies can tag every
                docket in the batch to the same job before Approve all
                (typical site-visit flow: 8 dockets → all "42 Smith St").
                Per-row overrides still win: if Review edited the job on a
                specific row, that value stays. Hidden when there are no
                jobs on the profile (nothing to pick from anyway). */}
            {allDone && readyCount > 0 && jobs.length > 0 && (
              <div className="pt-3">
                <label className="block text-[10px] font-heading font-bold uppercase tracking-wider text-white/40 mb-1.5">
                  Apply job to all ({readyCount})
                </label>
                <select
                  data-testid="photo-queue-batch-job"
                  value={batchJobId ?? ""}
                  onChange={(e) => setBatchJobId(e.target.value || null)}
                  className="w-full h-10 rounded-xl bi-input text-sm px-3"
                >
                  <option value="">
                    {activeJobId ? "Use active job" : "No job (unassigned)"}
                  </option>
                  {jobs.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </SheetHeader>

          <div className="px-5 pt-2 pb-4 space-y-2">
            {queue.map((q) => (
              <div
                key={q.id}
                data-testid={`photo-queue-item-${q.id}`}
                className="bi-surface p-3 flex items-center gap-3"
              >
                {q.thumb ? (
                  <img
                    src={q.thumb}
                    alt={q.filename}
                    className="w-14 h-14 rounded-lg object-cover shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-white/5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {q.status === "parsing" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/70 inline-flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Reading
                      </span>
                    )}
                    {q.status === "parsed" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#34D399]/15 text-[#34D399] inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Ready
                      </span>
                    )}
                    {q.status === "saving" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/70 inline-flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Saving
                      </span>
                    )}
                    {q.status === "saved" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Queued
                      </span>
                    )}
                    {q.status === "approved" && (
                      <span
                        data-testid={`photo-queue-approved-${q.id}`}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#34D399]/20 text-[#34D399] inline-flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3 h-3" /> In Ledger
                      </span>
                    )}
                    {q.status === "error" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F87171]/15 text-[#F87171] inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Failed
                      </span>
                    )}
                    <span className="text-xs text-white/50 truncate">{q.filename}</span>
                    {q.parsed?.personal_suggestion && (
                      <span
                        data-testid={`photo-queue-ai-personal-${q.id}`}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center gap-1"
                        title={q.parsed.personal_suggestion.reason || "AI thinks this is personal"}
                      >
                        <Sparkles className="w-3 h-3" /> Personal?
                      </span>
                    )}
                    {q.forcePersonal && !q.parsed?.personal_suggestion && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center gap-1">
                        <UserRound className="w-3 h-3" /> Personal
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-2">
                    <div className="font-heading font-extrabold text-base truncate">
                      {q.parsed?.supplier || (q.status === "parsing" ? "…" : "Unknown supplier")}
                    </div>
                    {q.parsed && (
                      <div className="text-xs text-white/60 tabular-nums shrink-0">
                        ${Number(q.parsed.total || 0).toFixed(2)}
                        {Number(q.parsed.gst) > 0 && (
                          <span className="text-white/40"> · GST ${Number(q.parsed.gst).toFixed(2)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {(q.status === "parsed" || q.status === "error") && (
                    <button
                      type="button"
                      onClick={() => toggleForcePersonal(q.id)}
                      data-testid={`photo-queue-personal-${q.id}`}
                      className={`h-9 px-2.5 rounded-lg text-xs inline-flex items-center gap-1 transition ${
                        q.forcePersonal || q.parsed?.personal_suggestion
                          ? "bg-[#FF9500]/20 text-[#FF9500] border border-[#FF9500]/40"
                          : "bi-ghost-btn text-white/70"
                      }`}
                      title="Mark as personal — opens Review with the personal toggle on"
                    >
                      <UserRound className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(q.status === "parsed" || q.status === "error") && (
                    <button
                      type="button"
                      onClick={() => openInReview(q)}
                      disabled={!q.parsed}
                      data-testid={`photo-queue-review-${q.id}`}
                      className="h-9 px-2.5 rounded-lg bi-ghost-btn text-xs inline-flex items-center gap-1 disabled:opacity-40"
                      title="Open in Review"
                    >
                      <PenLine className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {q.status === "parsed" && (
                    <button
                      type="button"
                      onClick={() =>
                        (q.forcePersonal || q.parsed?.personal_suggestion)
                          ? openInReview(q, { forcePersonal: true })
                          : saveAsPending(q)
                      }
                      data-testid={`photo-queue-save-${q.id}`}
                      className="h-9 px-2.5 rounded-lg bi-accent-btn text-xs inline-flex items-center gap-1"
                      title={
                        (q.forcePersonal || q.parsed?.personal_suggestion)
                          ? "Open in Review to save as personal"
                          : "Save as pending"
                      }
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {q.status !== "saving" && (
                    <button
                      type="button"
                      onClick={() => removeItem(q.id)}
                      data-testid={`photo-queue-remove-${q.id}`}
                      className="h-9 px-2 rounded-lg text-white/40 hover:text-red-300 transition"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="px-5 pb-5 grid grid-cols-2 gap-2 sticky bottom-0 bg-[#1F1F1F] pt-2">
            <button
              type="button"
              onClick={() => {
                queue.forEach((q) => q.thumb && URL.revokeObjectURL(q.thumb));
                setQueue([]);
                setQueueOpen(false);
              }}
              className="h-12 rounded-xl bi-ghost-btn text-sm"
              data-testid="photo-queue-close"
            >
              Close
            </button>
            <button
              type="button"
              onClick={saveAllReady}
              disabled={!allDone || readyCount === 0}
              className="h-12 rounded-xl bi-accent-btn text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="photo-queue-save-all"
            >
              <ArrowRight className="w-4 h-4" />
              Approve all {readyCount ? `(${readyCount})` : ""}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
